import axios from "axios";
import { collaborationSessionId } from "../collaboration/session";
import { getAccessToken } from "../slices/session";
import {
  isSentryReportingEnabled,
  SENTRY_REPORTING_HEADER,
  isSentryConfigured,
} from "../utils/sentry";

/** Resolved on each request from Redux (see store.tsx). */
let resolvePromangCompanyId = () => undefined;

export function setPromangApiCompanyIdResolver(fn) {
  resolvePromangCompanyId = typeof fn === "function" ? fn : () => undefined;
}

function isOAuthTokenRequest(request) {
  // Only Promang's public token endpoints skip the session. Microsoft account
  // linking (/microsoft/oauth/token) requires both Bearer and company context.
  try {
    const { pathname } = new URL(String(request?.url || ""), "http://promang.local");
    return /^(?:\/api\/v\d+)?\/oauth\/(token|revoke)\/?$/.test(pathname);
  } catch {
    return false;
  }
}

export function applyPromangApiRequestContext(request) {
  request.headers = request.headers || {};

  const skipAuthHeader =
    request.promangSkipAuthHeader === true || isOAuthTokenRequest(request);

  const hasAuthorization =
    request.headers["Authorization"] != null ||
    request.headers["authorization"] != null;
  if (!skipAuthHeader && !hasAuthorization) {
    const accessToken = getAccessToken();
    if (accessToken) {
      request.headers["Authorization"] = `Bearer ${accessToken}`;
    }
  }

  if (!request.promangSkipCompanyHeader && !isOAuthTokenRequest(request)) {
    try {
      const companyId = resolvePromangCompanyId();
      if (companyId != null && String(companyId).trim() !== "") {
        request.headers["X-Promang-Company-Id"] = String(companyId).trim();
      }
    } catch (_) {
      /* ignore */
    }
  }

  // When the platform dev menu has muted Sentry, tell the API to skip reporting
  // for this request as well (honoured only for platform-ops users server-side).
  if (isSentryConfigured()) {
    if (!isSentryReportingEnabled()) {
      request.headers[SENTRY_REPORTING_HEADER] = "0";
    } else {
      // Ensure any previously set suppress header is cleared when reporting is enabled
      delete request.headers[SENTRY_REPORTING_HEADER];
    }
  }

  delete request.promangSkipCompanyHeader;
  delete request.promangSkipAuthHeader;
  if (!skipAuthHeader && /^(post|put|patch|delete)$/i.test(request.method || "")) {
    request.headers["X-Promang-Collaboration-Session"] = collaborationSessionId();
  }

  // Let the browser set multipart boundary; default application/json breaks FormData uploads.
  if (typeof FormData !== "undefined" && request.data instanceof FormData) {
    delete request.headers["Content-Type"];
  }

  return request;
}

const instance = axios.create({
  baseURL: process.env.REACT_APP_BACKEND_URL,
  headers: {
    "Content-Type": "application/json",
    withCredentials: true,
  },
});

instance.interceptors.request.use(applyPromangApiRequestContext);

/**
 * Response interceptor policy (operation-feedback foundation):
 * - May attach correlation context for callers.
 * - Must NOT show toast.success / toast.error or own UI presentation.
 * - Must NOT unwrap every success to `.data` (blob/header consumers need full response).
 * Presentation belongs to operationFeedback (dialog / toast queue / form).
 */
instance.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error?.response?.status === 423 && error?.response?.data?.code === "TRAINING_REQUIRED" && typeof window !== "undefined") {
      window.dispatchEvent(new CustomEvent("promang:training-required", {
        detail: { companyId: error.config?.headers?.["X-Promang-Company-Id"] },
      }));
    }
    return Promise.reject(error);
  }
);

export default instance;
